import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { CareerIcon } from 'atoms';
import { APPLICATION_STATUSES, CareerApplicationStatus, ICareerApplication } from 'core';
import { formatCareerDate } from 'services/DateService';
import { careerLayout } from 'services/careerType';

/** Статусы, у которых срок контакта ещё что-то значит — как `APPLICATION_ACTIVE_STATUSES` сервера. */
const ACTIVE: CareerApplicationStatus[] = ['planned', 'applied', 'interview', 'offer'];

/** «Сегодня» по локальным часам студента, в формате даты сервера. */
const localToday = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Срок контакта прошёл: дата раньше сегодняшней, а отклик ещё в работе.
 * Строки `YYYY-MM-DD` сравниваются лексикографически ровно как даты.
 */
export const isFollowUpOverdue = (
  application: Pick<ICareerApplication, 'nextActionAt' | 'status'>,
  today: string = localToday(),
) =>
  Boolean(application.nextActionAt) &&
  ACTIVE.includes(application.status) &&
  application.nextActionAt < today;

interface ICareerApplicationCard {
  application: ICareerApplication;
  onStatus: (status: CareerApplicationStatus) => void;
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
}

/**
 * Один отклик в журнале.
 *
 * ⚠️ Статус меняется прямо здесь селектом, а не через форму: перевести отклик
 * из «Откликнулся» в «Интервью» — самое частое действие в журнале, и класть
 * его в модалку значит превратить одно движение в четыре.
 *
 * Подпись `imported` несущая: строка, перенесённая из старых самоотметок, не
 * заведена человеком, и выдавать её за собственную запись — то же тихое
 * враньё, что схлопнутый бейдж.
 */
export const CareerApplicationCard = memo(
  ({ application, onStatus, onEdit, onDelete, disabled }: ICareerApplicationCard) => {
    const { t } = useTranslation();

    return (
      <Card>
        <Head>
          <Body>
            <Company>{application.companyName}</Company>
            {application.position ? <Position>{application.position}</Position> : null}
            {application.applyUrl ? (
              <Link href={application.applyUrl} target="_blank" rel="noopener noreferrer">
                <CareerIcon name="externalLink" size={14} />
                {t('careerCenter.applications.link')}
              </Link>
            ) : null}
          </Body>

          <StatusPicker
            value={application.status}
            onChange={(value) => onStatus(value as CareerApplicationStatus)}
            // Подтверждённое школой трудоустройство не откатывается: сервер
            // откажет, и показывать управляемый селект значило бы обещать то,
            // чего не будет.
            disabled={disabled || application.isPlacementConfirmed}
            aria-label={t('careerCenter.applications.statusLabel')}
            options={APPLICATION_STATUSES.map((code) => ({
              value: code,
              label: t(`careerCenter.applications.status.${code}`),
            }))}
          />
        </Head>

        <Meta>
          {application.appliedAt ? (
            <MetaItem>
              <CareerIcon name="calendar" size={14} />
              {t('careerCenter.applications.appliedAt')}: {formatCareerDate(application.appliedAt)}
            </MetaItem>
          ) : null}
          <MetaItem>
            <CareerIcon name="clock" size={14} />
            {t('careerCenter.applications.followUp')}:{' '}
            {formatCareerDate(application.nextActionAt) ||
              t('careerCenter.applications.noFollowUp')}
          </MetaItem>
          {/* Просрочка — словом, не только цветом. */}
          {isFollowUpOverdue(application) ? (
            <Overdue>{t('careerCenter.applications.overdue')}</Overdue>
          ) : null}
          {application.source === 'import' ? (
            <Badge>{t('careerCenter.applications.imported')}</Badge>
          ) : null}
          {application.isPlacementConfirmed ? (
            <Badge>{t('careerCenter.applications.confirmed')}</Badge>
          ) : null}
        </Meta>

        {application.note ? <Note>{application.note}</Note> : null}

        <Actions>
          <Action type="button" onClick={onEdit} disabled={disabled}>
            {t('careerCenter.applications.edit')}
          </Action>
          <Action type="button" onClick={onDelete} disabled={disabled}>
            {t('careerCenter.applications.delete')}
          </Action>
        </Actions>
      </Card>
    );
  },
);

const Card = styled.article`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background: ${Colors.white};
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  ${mobile} {
    flex-direction: column;
  }
`;

const Body = styled.div`
  min-width: 0;
`;

const Company = styled.h3`
  margin: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const Position = styled.p`
  margin: 2px 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Link = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.extraSmall })}

  &:hover {
    color: ${Colors.black};
  }
`;

const StatusPicker = styled(Select)`
  flex: 0 0 auto;
  min-width: 170px;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  background: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Note = styled.p`
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
  white-space: pre-wrap;
`;

const Overdue = styled.span`
  padding: 2px 8px;
  border: 1px solid ${Colors.signal_red};
  border-radius: 10px;
  color: ${Colors.black};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const Action = styled.button`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${Colors.gray_dark};
  text-decoration: underline;
  ${fontService.text({ type: TextType.extraSmall })}

  &:hover:enabled {
    color: ${Colors.black};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;
